import { supabase } from './supabase/config';

export const DEFAULT_COMPOUNDS = {};
export const DEFAULT_HIDDEN = ['upper_abd', 'arm', 'glute'];

const LEGACY = {
  'L Abdomen': 'lower_abd:L',
  'R Abdomen': 'lower_abd:R',
  'L Abdomen (Mid)': 'lower_abd:Mid',
  'L Thigh': 'thigh:L',
  'R Thigh': 'thigh:R',
};
export const normSite = s => { if (!s) return null; if (s.includes(':')) return s; return LEGACY[s] || s; };

export const FRESH_STATE = {
  userShots: [],
  counters: {},
  compounds: {},
  compoundProtocols: {},
  hiddenZones: [...DEFAULT_HIDDEN],
  dashHiddenCmpds: [],
  vials: {},
  globalSyringeML: 1.0,
  darkMode: true,
  checkIns: [],
  checkInWeightMigrated: false,
  heightMigrated: false,
  userHeight: '',
  heightUnit: 'cm',
  userWeight: '',
  weightUnit: 'kg',
  bloodGlucoseUnit: 'mg/dL',
  doseUnit: 'mL',
  userGender: '',
  checkInsEnabled: false,
  schedulingEnabled: true,
  notificationsEnabled: false,
  customZones: [],
  streakEnabled: true,
  streakReminderEnabled: true,
  streakSaves: 0,
  pillStreakSaves: 0,
  streakShieldedDates: [],
  pillStreakShieldedDates: [],
  badges: [],
  timezone: '',
};

export async function loadFromFirestore(uid) {
  const { data, error } = await supabase
    .from('user_data')
    .select('data')
    .eq('id', uid)
    .single();

  // PGRST116 = .single() matched no row — a genuinely new user with no saved data yet.
  // Any other error (network blip, RLS, transient 5xx) must throw instead of returning
  // null the same way: the caller falls back to a fresh/empty state on a null return and
  // then auto-saves it, so conflating "new user" with "load failed" silently overwrites
  // a real user's data whenever a transient error is mistaken for a first-time signup.
  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  if (!data) return null;
  const p = data.data;
  const userShots = (p.userShots || p.shots || []).map(s => ({
    ...s,
    compound: s.compound || 'nad',
    site: normSite(s.site),
    vol: String(s.vol || '').replace('ml', ''),
  }));
  const compounds = {};
  if (p.customCompounds) Object.keys(p.customCompounds).forEach(k => { compounds[k] = { ...p.customCompounds[k] }; });
  if (p.compounds) Object.keys(p.compounds).forEach(k => { compounds[k] = { ...p.compounds[k] }; });
  const counters = { ...p.counters };
  Object.keys(compounds).forEach(k => { if (!(k in counters)) counters[k] = 0; });
  return {
    userShots,
    counters,
    compounds,
    compoundProtocols: { ...(p.compoundProtocols || {}) },
    hiddenZones: p.hiddenZones || [...DEFAULT_HIDDEN],
    dashHiddenCmpds: p.dashHiddenCmpds || [],
    vials: { ...(p.vials || {}) },
    globalSyringeML: p.globalSyringeML || 1.0,
    darkMode: p.darkMode !== undefined ? p.darkMode : true,
    checkIns: p.checkIns || [],
    checkInWeightMigrated: p.checkInWeightMigrated || false,
    heightMigrated: p.heightMigrated || false,
    userHeight: p.userHeight || '',
    heightUnit: p.heightUnit || 'cm',
    userWeight: p.userWeight || '',
    weightUnit: p.weightUnit || 'kg',
    bloodGlucoseUnit: p.bloodGlucoseUnit || 'mg/dL',
    doseUnit: p.doseUnit || 'mL',
    userGender: p.userGender || '',
    checkInsEnabled: p.checkInsEnabled || false,
    schedulingEnabled: p.schedulingEnabled !== undefined ? p.schedulingEnabled : true,
    notificationsEnabled: p.notificationsEnabled || false,
    badges: p.badges || [],
    customZones: p.customZones || [],
    streakEnabled: p.streakEnabled !== undefined ? p.streakEnabled : true,
    streakReminderEnabled: p.streakReminderEnabled !== undefined ? p.streakReminderEnabled : true,
    streakSaves: p.streakSaves ?? 0,
    pillStreakSaves: p.pillStreakSaves ?? 0,
    streakShieldedDates: p.streakShieldedDates || [],
    pillStreakShieldedDates: p.pillStreakShieldedDates || [],
    timezone: p.timezone || '',
    lotPromptDismissedAt: p.lotPromptDismissedAt || '',
  };
}

export async function saveToFirestore(uid, s) {
  const raw = {
    userShots: s.userShots || [],
    counters: s.counters ?? {},
    customCompounds: Object.fromEntries(
      Object.entries(s.compounds || {})
        .filter(([k]) => !DEFAULT_COMPOUNDS[k])
        .map(([k, v]) => {
          const { protocols, ...rest } = v;
          return [k, Object.fromEntries(Object.entries(rest).filter(([, val]) => val !== undefined))];
        })
    ),
    compoundOverrides: Object.fromEntries(
      Object.entries(s.compounds || {})
        .filter(([k]) => DEFAULT_COMPOUNDS[k])
        .map(([k, v]) => [k, Object.fromEntries(
          Object.entries({
            goal: v.goal, color: v.color, dim: v.dim, text: v.text,
            syringeML: v.syringeML, notes: v.notes, freq: v.freq,
            shortLabel: v.shortLabel, label: v.label,
            reorderDays: v.reorderDays,
          }).filter(([, val]) => val !== undefined)
        )])
    ),
    compoundProtocols: s.compoundProtocols ?? {},
    hiddenZones: s.hiddenZones ?? [],
    dashHiddenCmpds: s.dashHiddenCmpds ?? [],
    vials: s.vials ?? {},
    globalSyringeML: s.globalSyringeML ?? 1.0,
    darkMode: s.darkMode ?? true,
    checkIns: s.checkIns || [],
    checkInWeightMigrated: s.checkInWeightMigrated || false,
    heightMigrated: s.heightMigrated || false,
    userHeight: s.userHeight || '',
    heightUnit: s.heightUnit || 'cm',
    userWeight: s.userWeight || '',
    weightUnit: s.weightUnit || 'kg',
    bloodGlucoseUnit: s.bloodGlucoseUnit || 'mg/dL',
    doseUnit: s.doseUnit || 'mL',
    userGender: s.userGender || '',
    checkInsEnabled: s.checkInsEnabled || false,
    schedulingEnabled: s.schedulingEnabled !== undefined ? s.schedulingEnabled : true,
    notificationsEnabled: s.notificationsEnabled || false,
    badges: s.badges ?? [],
    customZones: s.customZones ?? [],
    streakEnabled: s.streakEnabled !== undefined ? s.streakEnabled : true,
    streakReminderEnabled: s.streakReminderEnabled !== undefined ? s.streakReminderEnabled : true,
    streakSaves: s.streakSaves ?? 0,
    pillStreakSaves: s.pillStreakSaves ?? 0,
    streakShieldedDates: s.streakShieldedDates ?? [],
    pillStreakShieldedDates: s.pillStreakShieldedDates ?? [],
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    lotPromptDismissedAt: s.lotPromptDismissedAt || undefined,
  };
  const slim = Object.fromEntries(Object.entries(raw).filter(([, v]) => v !== undefined));
  const { error } = await supabase
    .from('user_data')
    .upsert({ id: uid, data: slim, updated_at: new Date().toISOString() });
  if (error) throw error;
}


export async function getOrCreateInviteCode(uid) {
  const { data } = await supabase.from('invites').select('code').eq('created_by', uid).limit(1).maybeSingle();
  if (data) return data.code;
  const code = Math.random().toString(36).substring(2, 10).toUpperCase();
  const { error } = await supabase.from('invites').insert({ code, created_by: uid });
  if (error) throw error;
  return code;
}

export async function redeemInviteCode(code, uid) {
  const { data: authData } = await supabase.auth.getUser();
  const createdAt = new Date(authData?.user?.created_at);
  const ageMinutes = (Date.now() - createdAt) / 60000;
  if (ageMinutes > 10) return null;

  const { data, error } = await supabase
    .from('invites')
    .update({ used_by: uid, used_at: new Date().toISOString() })
    .eq('code', code)
    .is('used_by', null)
    .neq('created_by', uid)
    .select('created_by')
    .single();
  if (error || !data) return null;
  return data.created_by;
}

export async function getInviteCount(uid) {
  const { count } = await supabase
    .from('invites')
    .select('*', { count: 'exact', head: true })
    .eq('created_by', uid)
    .not('used_by', 'is', null);
  return count || 0;
}


